import { NativeStackScreenProps } from "@react-navigation/native-stack";
import {
  Divider,
  FlatListSearchable,
  Page,
  Button,
  useFlashMessages,
  DEFAULT_PAGE_WIDTH,
  TextButton,
} from "@towersystems/roam-common-native";
import useOnline from "@towersystems/roam-common-native/lib/utilities/use-online";
import {
  AccountFeatureFlag,
  Customer,
} from "@towersystems/roam-common/lib/generated-types";
import { Spacings, Text } from "react-native-ui-lib";
import { CustomerRootParamList } from "../../../../types";
import CustomerModel from "../../../../utilities/data/model/Customer";
import { useSaleControllerProvider } from "../../utils/sale-controller";
import { CustomerListItemRenderer } from "./components";
import { useActionBackOrClose } from "../../utils/use-action-back-or-close";
import { useCustomerRootScreenActionsContextProvider } from "../CustomerRootScreen/hooks";
import { CustomerFilterValues } from "../../../customer/types";
import { useListFilterContext } from "../../../../utilities/list-filter/use-list-filter-context";
import { QUERY_CUSTOMER_LIST } from "../../../customer/utils/customer";
import { useLoadMoreList } from "../../../../utilities/load-more-list";
import { customerFilterValuesToApiInput } from "../../../customer";
import { SearchContext, useSearch } from "../../../../utilities/search";
import { useScheduler } from "../../../../utilities/scheduler";
import React from "react";
import { useFocusEffect } from "@react-navigation/native";
import { useTranslation } from "react-i18next";
import { AntDesign } from "@expo/vector-icons";
import { useCurrentVendorProvider } from "../../../../utilities/vendor";
import { vendorCanAccessFeature } from "../../../../utilities/feature-flags";
import { useOfflineCustomerSearch } from "../../../../utilities/customer";

export type CustomerListScreenProps = NativeStackScreenProps<
  CustomerRootParamList,
  "CustomerListScreen"
>;

export function CustomerListScreen({ navigation }: CustomerListScreenProps) {
  const { t } = useTranslation("customer");

  const maybeContext = useCustomerRootScreenActionsContextProvider();
  const online = useOnline();

  const { sale } = useSaleControllerProvider();
  const { showMessage } = useFlashMessages();
  useActionBackOrClose(navigation);
  const search = useSearch();
  const { schedule } = useScheduler(500);
  const { currentVendor } = useCurrentVendorProvider();

  const { filterState, watch } = useListFilterContext<CustomerFilterValues>();
  const { activeFiltersCount } = filterState;

  const watchedFilterValues = watch();

  const loadMoreList = useLoadMoreList<Customer>({
    initialLoad: false,
    query: QUERY_CUSTOMER_LIST,
    resultKey: "customers",
    sort: { updatedAt: "DESC" },
    variableFormatter: (variables) => {
      return {
        options: {
          ...variables,
        },
      };
    },
  });

  useFocusEffect(
    React.useCallback(() => {
      navigation.setOptions({
        headerTitle() {
          return (
            <TextButton
              iconStyle={{ marginRight: 0 }}
              icon={<AntDesign name="filter" size={20} />}
              onPress={() => {
                navigation.navigate("CustomerListFilterRootScreen", {
                  screen: "CustomerListFilterScreen",
                });
              }}
            >
              {t("common:filter", {
                count: activeFiltersCount,
              })}
            </TextButton>
          );
        },
      });
    }, [activeFiltersCount]),
  );
  const { data: offlineData, searchCustomers } = useOfflineCustomerSearch();

  React.useEffect(() => {
    if (!online) {
      searchCustomers(search.query);
    } else if (loadMoreList.called) {
      schedule(() => handleLoadMore(true));
    }
  }, [search.query, online]);

  const handleLoadMore = (reset?: boolean) => {
    loadMoreList.loadMore(
      {
        filter: {
          term: { contains: search.query },
          ...customerFilterValuesToApiInput(watchedFilterValues),
        },
      },
      reset,
    );
  };

  useFocusEffect(
    React.useCallback(() => {
      handleLoadMore(true);
    }, [watchedFilterValues]),
  );

  const renderItem = React.useCallback(
    ({ item }: { item: Customer | CustomerModel }) => {
      return (
        <CustomerListItemRenderer
          customer={item}
          retailerCustAccountOnly={maybeContext?.retailerCustAccountOnly}
          onAction={async () => {
            if (maybeContext?.onCustomerSelected) {
              await maybeContext.onCustomerSelected(item.id);
              return;
            }
            if (sale) {
              try {
                await sale.addCustomer(item.id);
                navigation.pop();
              } catch (err) {
                showMessage({
                  message: "Error adding customer to sale",
                  severity: "error",
                });
              }
            }
          }}
        />
      );
    },
    [sale],
  );

  const normalizedData = online
    ? {
        items: loadMoreList.items.map((item) => item),
        loading: loadMoreList.loading,
      }
    : { items: offlineData.map((i) => i as any), loading: false };

  console.log("CustomerListScreen normalizedData", normalizedData);

  const emptyComponent =
    search.query && !normalizedData.loading ? (
      <Text>No customers to show</Text>
    ) : undefined;

  React.useEffect(() => {
    if (true == maybeContext?.hideNewCustomerButton) {
      return;
    }
    if (
      vendorCanAccessFeature(
        AccountFeatureFlag.POS_APP_MANAGE_CUSTOMERS_UI,
        currentVendor,
      )
    ) {
      navigation.setOptions({
        headerRight: ({}) => (
          <Button
            isAction
            size="compact"
            label="New Customer"
            disabled={!online}
            onPress={() => navigation.navigate("CreateCustomerScreen")}
          ></Button>
        ),
      });
    }
  }, [online, maybeContext?.hideNewCustomerButton]);

  return (
    <SearchContext.Provider value={search}>
      <Page scrollable={false} horizontalPadding={0} fullWidth>
        <FlatListSearchable
          contentContainerStyle={{
            maxWidth: DEFAULT_PAGE_WIDTH,
            width: "100%",
            alignSelf: "center",
            paddingHorizontal: Spacings.s5,
            paddingTop: Spacings.s5,
          }}
          placeholder="Search..."
          onChange={(query) => search.setQuery(query)}
          value={search.query}
          ItemSeparatorComponent={Divider}
          renderItem={renderItem}
          refreshing={normalizedData.loading}
          data={normalizedData.items}
          scrollEnabled={true}
          ListEmptyComponent={emptyComponent}
          nestedScrollEnabled
          onEndReachedThreshold={0.3}
          onEndReached={() => {
            normalizedData.items.length >= 1 &&
              handleLoadMore &&
              handleLoadMore(false);
          }}
        />
      </Page>
    </SearchContext.Provider>
  );
}
